import {
  Button,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import Edit02 from '@untitled-ui/icons-react/build/esm/Edit02';
import moment from 'moment';
import Label from '../Label';
import { DeleteOutlined } from '@mui/icons-material';
import { getAssessmentTypeLabel, getNursingDisplayValue } from 'src/utils/mdsConstants';

type MdsAssessment = {
  uuid?: string;
  assessmentType?: string;
  assessmentDate?: string;
  mdsPtOtGroup?: string;
  mdsSlpGroup?: string;
  mdsNursingGroup?: string;
  mdsNtaGroup?: string;
  isFinalMds?: boolean;
  finalMds?: {
    assessmentType?: string;
    assessmentDate?: string;
    mdsPtOtGroup?: string;
    mdsSlpGroup?: string;
    mdsNursingGroup?: string;
    mdsNtaGroup?: string;
  };
  status?: string;
  [key: string]: any;
};

type Props = {
  mdsAssessments: MdsAssessment[];
  handleEditMds?: (mds: MdsAssessment) => void;
  handleDeleteMds?: (mds: MdsAssessment) => void;
  showActions?: boolean;
  onNewAssessmentClick?: () => void;
  doesPatientHaveOpenEOC?: boolean;
  hasUpdatePatientPermission?: boolean;
};

const getAssessmentStatus = (mds: MdsAssessment): string => {
  if (mds?.isFinalMds) {
    return 'Final';
  }
  return 'Initial';
};

const getStatusLabelProps = (status: string) => {
  const statusUpper = status.toUpperCase();

  switch (status) {
    case 'Initial':
      return {
        backgroundColor: '#FEF3C7',
        color: '#92400E',
        text: statusUpper,
      };

    case 'Final':
      return {
        backgroundColor: '#D1FAE5',
        color: '#065F46',
        text: statusUpper,
      };
    default:
      return {
        backgroundColor: '#F8F9FA',
        color: '#6B7280',
        text: statusUpper,
      };
  }
};

const MdsAssessmentTable = (props: Props) => {
  const {
    mdsAssessments = [],
    handleEditMds,
    handleDeleteMds,
    showActions = true,
    onNewAssessmentClick,
    doesPatientHaveOpenEOC,
    hasUpdatePatientPermission,
  } = props;

  const hasAssessments = mdsAssessments && mdsAssessments.length > 0;

  return (
    <Stack>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            <TableCell>Assessment Reference Date</TableCell>
            <TableCell>Assessment Type</TableCell>
            <TableCell>PT + OT CMG</TableCell>
            <TableCell>SLP CMG</TableCell>
            <TableCell>Nursing CMG</TableCell>
            <TableCell>NTA CMG</TableCell>
            <TableCell>Status</TableCell>
            {showActions && (handleEditMds || handleDeleteMds) && (
              <TableCell align="right">Actions</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {hasAssessments ? (
            mdsAssessments.map((mds: MdsAssessment, index: number) => {
              const assessmentDate = mds?.isFinalMds
                ? mds?.finalMds?.assessmentDate
                : mds?.assessmentDate;

              const assessmentType = mds?.isFinalMds
                ? mds?.finalMds?.assessmentType
                : mds?.assessmentType;

              const ptOtGroup = mds?.isFinalMds ? mds?.finalMds?.mdsPtOtGroup : mds?.mdsPtOtGroup;

              const slpGroup = mds?.isFinalMds ? mds?.finalMds?.mdsSlpGroup : mds?.mdsSlpGroup;

              const nursingGroup = mds?.isFinalMds
                ? mds?.finalMds?.mdsNursingGroup
                : mds?.mdsNursingGroup;

              const ntaGroup = mds?.isFinalMds ? mds?.finalMds?.mdsNtaGroup : mds?.mdsNtaGroup;

              const status = getAssessmentStatus(mds);
              const statusProps = getStatusLabelProps(status);

              return (
                <TableRow key={mds?.uuid || index}>
                  <TableCell>
                    {assessmentDate
                      ? moment(
                          new Date(
                            new Date(assessmentDate)?.valueOf() +
                              new Date(assessmentDate)?.getTimezoneOffset() * 60 * 1000
                          )
                        ).format('MMM DD, YYYY')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {assessmentType ? getAssessmentTypeLabel(assessmentType) : '-'}
                  </TableCell>
                  <TableCell>{ptOtGroup || '-'}</TableCell>
                  <TableCell>{slpGroup || '-'}</TableCell>
                  <TableCell>
                    {nursingGroup ? getNursingDisplayValue(nursingGroup) : '-'}
                  </TableCell>
                  <TableCell>{ntaGroup || '-'}</TableCell>
                  <TableCell>
                    <Label
                      backgroundColor={statusProps.backgroundColor}
                      color={statusProps.color}
                      text={statusProps.text}
                    />
                  </TableCell>
                  {showActions && (handleEditMds || handleDeleteMds) && (
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        {handleEditMds && (
                          <Tooltip
                            title={
                              !hasUpdatePatientPermission
                                ? 'You do not have permission to update the patient details'
                                : 'Edit MDS'
                            }
                            followCursor
                          >
                            <span>
                              <IconButton
                                onClick={() => handleEditMds(mds)}
                                size="small"
                                disabled={!hasUpdatePatientPermission}
                              >
                                <SvgIcon>
                                  <Edit02 />
                                </SvgIcon>
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {handleDeleteMds && (
                          <Tooltip
                            title={
                              !hasUpdatePatientPermission
                                ? 'You do not have permission to update the patient details'
                                : 'Delete MDS'
                            }
                            followCursor
                          >
                            <span>
                              <IconButton
                                onClick={() => handleDeleteMds(mds)}
                                size="small"
                                disabled={!hasUpdatePatientPermission}
                              >
                                <SvgIcon>
                                  <DeleteOutlined />
                                </SvgIcon>
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={showActions && (handleEditMds || handleDeleteMds) ? 8 : 7}>
                <Box
                  textAlign="center"
                  p={4}
                >
                  <Typography
                    color={'text.secondary'}
                    variant="subtitle1"
                    sx={{ mb: 1 }}
                  >
                    No MDS assessments found for this patient.
                  </Typography>
                  {!doesPatientHaveOpenEOC && (
                    <Typography
                      color={'text.secondary'}
                      variant="subtitle1"
                      sx={{ mb: 1 }}
                    >
                      Create an episode of care, and then click <b>+ New Assessment</b> to get
                      started.
                    </Typography>
                  )}
                  {onNewAssessmentClick && doesPatientHaveOpenEOC && (
                    <Typography
                      color={'text.secondary'}
                      variant="body2"
                    >
                      Click{' '}
                      <Button
                        variant="text"
                        color="primary"
                        onClick={onNewAssessmentClick}
                        sx={{
                          textTransform: 'none',
                          textDecoration: 'underline',
                          p: 0,
                          minWidth: 'auto',
                        }}
                        disabled={!doesPatientHaveOpenEOC || !hasUpdatePatientPermission}
                      >
                        <b>+ New Assessment</b>
                      </Button>{' '}
                      to get started.
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Stack>
  );
};

export default MdsAssessmentTable;
